import React, { useState } from "react";
import { translations } from "../translations";
import { checkPhoneSubmitted } from "../services/api";
import { getMaxDobForMinAge, isAtLeastMinAge } from "../utils/date";

const MIN_AGE = 18;
const MAX_DOB = getMaxDobForMinAge(MIN_AGE);

const CandidateForm = ({ lang, onSubmit }) => {
    const t = translations[lang];
    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [isCheckingPhone, setIsCheckingPhone] = useState(false);
    const [formData, setFormData] = useState({
        fullName: "",
        phoneNumber: "",
        altPhoneNumber: "",
        currentAddress: "",
        permanentAddress: "",
        aadharNumber: "",
        dob: "",
        gender: "",
        education: "",
        medicalExp: "",
        totalExp: "",
        prevCompany: "",
        currentSalary: "",
        expectedSalary: "",
        availability: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;

        if (["phoneNumber", "altPhoneNumber", "aadharNumber", "currentSalary", "expectedSalary"].includes(name)) {
            finalValue = value.replace(/\D/g, "");
        }

        if (name === "fullName") {
            finalValue = value.replace(/[^a-zA-Z\s]/g, "");
        }

        if (name === "phoneNumber" || name === "altPhoneNumber") finalValue = finalValue.slice(0, 10);
        if (name === "aadharNumber") finalValue = finalValue.slice(0, 12);

        setFormData((prev) => ({ ...prev, [name]: finalValue }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const validateStep = () => {
        const newErrors = {};
        if (step === 1) {
            if (!formData.fullName.trim()) newErrors.fullName = t.fieldRequired;
            if (formData.phoneNumber.length !== 10) newErrors.phoneNumber = t.phoneRequired;
        }
        if (step === 2) {
            if (!formData.currentAddress.trim()) newErrors.currentAddress = t.fieldRequired;
            if (formData.aadharNumber.length !== 12) newErrors.aadharNumber = t.aadharRequired;
            if (!formData.dob) newErrors.dob = t.fieldRequired;
            else if (!isAtLeastMinAge(formData.dob, MIN_AGE)) newErrors.dob = t.dobMinAge;
            if (!formData.gender) newErrors.gender = t.fieldRequired;
        }
        if (step === 3) {
            if (!formData.education.trim()) newErrors.education = t.fieldRequired;
            if (!formData.medicalExp) newErrors.medicalExp = t.fieldRequired;
            if (!formData.expectedSalary) newErrors.expectedSalary = t.fieldRequired;
            if (!formData.availability) newErrors.availability = t.fieldRequired;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep()) {
            setStep(step + 1);
        }
    };

    const prevStep = () => setStep(step - 1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep()) return;

        setIsCheckingPhone(true);
        setErrors((prev) => ({ ...prev, phoneNumber: null }));

        try {
            const exists = await checkPhoneSubmitted(formData.phoneNumber);
            if (exists) {
                setErrors((prev) => ({ ...prev, phoneNumber: t.duplicatePhone }));
                setStep(1);
                return;
            }
            onSubmit(formData);
        } catch {
            setErrors((prev) => ({ ...prev, phoneNumber: t.phoneCheckFailed }));
            setStep(1);
        } finally {
            setIsCheckingPhone(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="form-step-content">
                        <label>{t.fullName}</label>
                        <input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Full Name (Text only)" />
                        {errors.fullName && <span className="error-text">{errors.fullName}</span>}

                        <label>{t.phoneNumber}</label>
                        <input name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="10 Digit Number" />
                        {errors.phoneNumber && <span className="error-text">{errors.phoneNumber}</span>}

                        <label>{t.altPhoneNumber}</label>
                        <input name="altPhoneNumber" value={formData.altPhoneNumber} onChange={handleChange} placeholder="Optional 10 Digit" />
                    </div>
                );
            case 2:
                return (
                    <div className="form-step-content">
                        <label>{t.currentAddress}</label>
                        <input name="currentAddress" value={formData.currentAddress} onChange={handleChange} />
                        {errors.currentAddress && <span className="error-text">{errors.currentAddress}</span>}

                        <label>{t.permanentAddress}</label>
                        <input name="permanentAddress" value={formData.permanentAddress} onChange={handleChange} />

                        <label>{t.aadharNumber}</label>
                        <input name="aadharNumber" value={formData.aadharNumber} onChange={handleChange} placeholder="12 Digit Aadhar" />
                        {errors.aadharNumber && <span className="error-text">{errors.aadharNumber}</span>}

                        <label>{t.dob}</label>
                        <input type="date" name="dob" value={formData.dob} onChange={handleChange} max={MAX_DOB} />
                        {errors.dob && <span className="error-text">{errors.dob}</span>}

                        <label>{t.gender}</label>
                        <select name="gender" value={formData.gender} onChange={handleChange}>
                            <option value="">Select</option>
                            <option value="Male">{t.male}</option>
                            <option value="Female">{t.female}</option>
                            <option value="Other">{t.other}</option>
                        </select>
                        {errors.gender && <span className="error-text">{errors.gender}</span>}
                    </div>
                );
            case 3:
                return (
                    <div className="form-step-content">
                        <label>{t.education}</label>
                        <input name="education" value={formData.education} onChange={handleChange} />
                        {errors.education && <span className="error-text">{errors.education}</span>}

                        <label>{t.medicalExp}</label>
                        <select name="medicalExp" value={formData.medicalExp} onChange={handleChange}>
                            <option value="">Select</option>
                            <option value="Yes">{t.yes}</option>
                            <option value="No">{t.no}</option>
                        </select>
                        {errors.medicalExp && <span className="error-text">{errors.medicalExp}</span>}

                        {formData.medicalExp === "Yes" && (
                            <>
                                <label>{t.totalExp}</label>
                                <select name="totalExp" value={formData.totalExp} onChange={handleChange}>
                                    <option value="">Select</option>
                                    <option value="Fresher">{t.fresher}</option>
                                    <option value="0-1 year">{t.exp01}</option>
                                    <option value="1-3 years">{t.exp13}</option>
                                    <option value="3+ years">{t.exp3plus}</option>
                                </select>

                                <label>{t.prevCompany}</label>
                                <input name="prevCompany" value={formData.prevCompany} onChange={handleChange} />

                                <label>{t.currentSalary}</label>
                                <input name="currentSalary" value={formData.currentSalary} onChange={handleChange} placeholder="Numbers only" />
                            </>
                        )}

                        <label>{t.expectedSalary}</label>
                        <input name="expectedSalary" value={formData.expectedSalary} onChange={handleChange} placeholder="Numbers only" />
                        {errors.expectedSalary && <span className="error-text">{errors.expectedSalary}</span>}

                        <label>{t.availability}</label>
                        <select name="availability" value={formData.availability} onChange={handleChange}>
                            <option value="">Select</option>
                            <option value="Immediate">{t.immediate}</option>
                            <option value="7 days">{t.days7}</option>
                            <option value="15 days">{t.days15}</option>
                            <option value="30 days">{t.days30}</option>
                        </select>
                        {errors.availability && <span className="error-text">{errors.availability}</span>}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="page-wrap">
            <div className="card form-card">
                <div className="form-header">
                    <h2>{t.personalDetails}</h2>
                </div>

                <div className="form-step-nav">
                    {[
                        { num: 1, label: t.formStep1 },
                        { num: 2, label: t.formStep2 },
                        { num: 3, label: t.formStep3 },
                    ].map(({ num, label }) => (
                        <div key={num} className={`form-step-item ${step >= num ? "active" : ""} ${step > num ? "done" : ""}`}>
                            <span className="form-step-circle">{step > num ? "✓" : num}</span>
                            <span className="form-step-label">{label}</span>
                        </div>
                    ))}
                </div>

                <div className="progress-bar">
                    <div className="progress" style={{ width: `${(step / 3) * 100}%` }} />
                </div>

                <div className="form-body">
                    {renderStep()}
                </div>

                <div className="form-actions">
                    {step > 1 && (
                        <button type="button" onClick={prevStep} className="btn-secondary">
                            ← {t.previous}
                        </button>
                    )}
                    {step < 3 ? (
                        <button type="button" className="btn-primary" onClick={nextStep}>
                            {t.next} <span className="btn-arrow">→</span>
                        </button>
                    ) : (
                        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={isCheckingPhone}>
                            {isCheckingPhone ? t.checkingPhone : t.submit}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CandidateForm;
